'use client'

import classNames from 'classnames'
import React, { FunctionComponent, PropsWithChildren, useContext, useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import styles from './TruncatedTextContainer.module.css'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import { safeScroll } from '@/utils/navigation.utils'
import { CLASSES, GLOBAL_CLASSES } from '@/config/html-attributes.config'
import { Button } from '@/app/_components/Button'
import { EVENT_DESCRIPTION_SELECTOR } from '@/config/events.config'
import TXT from '@/messages'

type Props = {
  className?: string
  truncateContent?: boolean
}

const TruncatedTextContainer: FunctionComponent<PropsWithChildren<Props>> = ({
  className = '',
  children,
  truncateContent = true,
}: PropsWithChildren<Props>) => {
  const { mobile } = useContext(ThemeContext)
  const [truncated, setTruncated] = useState(truncateContent)
  const timeoutRef = useRef<NodeJS.Timeout>(null)

  const pathname = usePathname()
  const isEvent = pathname.includes('events')

  const isMobileTruncated = truncated && mobile
  const initialRender = useRef(true)
  const truncatedContentRef = useRef<HTMLDivElement>(null)

  /* As exhibition text container is being reworked in the near future
  to separate out description/location/estimated pricing/hours of operation
  into separate containers, this will only be used for event details  */
  useEffect(() => {
    const bodyRect = document.body.getBoundingClientRect()
    const elemRect = isEvent
      ? truncatedContentRef.current?.getBoundingClientRect()
      : truncatedContentRef.current?.querySelector(EVENT_DESCRIPTION_SELECTOR)?.getBoundingClientRect()
    if (elemRect) {
      const offset = elemRect.top - bodyRect.top
      if (truncatedContentRef.current && mobile && !initialRender.current) {
        timeoutRef.current = setTimeout(() => {
          safeScroll(offset - 100)
        }, 0)
      }
    }
    initialRender.current = false
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isMobileTruncated])

  return (
    <div
      ref={truncatedContentRef}
      className={classNames(className, 'truncated-content', {
        truncated: isMobileTruncated,
        // hacky, but that's the only place we don't want it
        [GLOBAL_CLASSES.TypoStandard]: !className?.includes(CLASSES.PURCHASE_CONFIRMATION),
        mobile,
      })}
    >
      <div className={classNames(
        styles.ContentInner,
        { [styles.Truncated]: isMobileTruncated },
      )}
      >
        {children}
      </div>
      {mobile && truncateContent && (
        <div className={styles.ReadMoreContainer}>
          <Button
            size="xs"
            weight="bold"
            onClick={() => setTruncated(!truncated)}
            testId="read-more"
            uppercase
          >
            {truncated ? TXT.buttons.readMore : TXT.buttons.readLess}
          </Button>
        </div>
      )}
    </div>
  )
}

export default TruncatedTextContainer
